/**
 * The wooden house across the moat: five boxes and a gable, and not one collider.
 *
 * It is here to answer a question the grounds otherwise leave open. A maze ringed by water
 * with nothing beyond it reads as a level that stops at its own edge; a house standing on
 * the far bank says the world carries on and the player merely cannot get there. That is
 * the whole job, and it is why this is scenery in the strictest sense — no rigid body, no
 * sensor, no interaction, nothing that can change where the player may walk.
 *
 * **North, the exit side.** The player spawns south and walks the maze away from
 * themselves; putting the house at the far end means the reward for solving it is a view
 * of somewhere, and the exit gap frames it. It is aligned on the exit column for the same
 * reason, so walking out points you straight at it.
 *
 * **Where, exactly** is derived from the exterior fit rather than authored: `houseBandRadius`
 * is where the far bank's scenery stands, and the pure layer owns that number because it is
 * the one that has to sit in order with all the others — past the moat, in front of the
 * woods, well inside the backdrop (see `buildExterior`'s grounds fractions).
 *
 * It used to be squeezed between the near rain shell and the treeline, and that band was
 * about five metres wide, which is why this is a cottage seen broadside rather than a hall.
 * Both of those edges are gone: outdoors it does not rain (design D9), so nothing hangs
 * between here and the woods, and the woods themselves have moved out behind the house
 * instead of in front of it. What decides the distance now is how big the building should
 * read — about 21 m past the bank the player stands on, where it subtends a quarter of the
 * view. At the old distance it was half of it, and a house that large across a ditch reads
 * as a wall rather than as somewhere else.
 *
 * That distance is barely hazed when the player comes out of the exit and looks across at
 * it, and deep into the fog from anywhere back in the maze — which only works because the
 * outdoor fog runs to 65 m rather than 45 (`GameCanvas`). At the indoor range this would be
 * a flat grey rectangle.
 *
 * It casts no shadow. The budget for a grounds level is one shadow map (design D5), fitted
 * to the maze, and a building outside that frustum would need a second one to itself.
 */

import { Instance, Instances } from '@react-three/drei';
import { buildExterior } from '../game/profile/buildExterior';
import type { BuiltGrounds, BuiltLevel } from '../game/profile/buildGeometry';
import { boxMin, type Box } from '../game/profile/types';
import { GROUND_DROP } from './ExteriorWorld';
import { surfaceMaterial, toonMaterial } from './materials';

type Props = {
  built: BuiltLevel;
  grounds: BuiltGrounds;
};

/**
 * Roughly a real one-storey house — the profile's own rooms run 4–6 m. Seen broadside:
 * the ridge runs along its width, so what faces the maze is the long wall with the door
 * in it rather than a gable end.
 */
const WIDTH = 9;
const DEPTH = 4.5;
const WALL_HEIGHT = 3.2;

/** Eaves stand proud of the walls, along the sides and at the gable ends alike. */
const EAVE = 0.5;
const ROOF_HALF_WIDTH = DEPTH / 2 + EAVE;
/** Flat face to ridge. About a 39° pitch over that span — steep enough to shed a storm. */
const ROOF_RISE = 2.2;

/**
 * A three-sided cylinder is an equilateral triangular prism, which is a gable roof once it
 * is laid on its side and squashed to the pitch you actually want. Half its base is
 * `sin(120°)` of its radius and its apex stands `1.5` radii above the opposite face, so
 * these two numbers turn a unit prism into any roof.
 */
const PRISM_HALF_BASE = Math.sin((2 * Math.PI) / 3);
const PRISM_HEIGHT = 1.5;

/**
 * Where it stands if the fit has no band for it — which cannot happen, since a fit built
 * from a level with grounds always has one and this is only mounted on grounds. It is a
 * floor rather than a fallback: the one thing that must never happen is a house in the moat.
 */
const MIN_GAP = 1;

/** A few degrees off the grid: a building sited in a landscape, not stamped onto it. */
const YAW = 0.14;

const WOOD_COLOR = '#6b4a2f';
const ROOF_COLOR = '#2a2f38';
const CHIMNEY_COLOR = '#4a4038';
const DOOR_COLOR = '#241a12';
/** Unlit, so a lamp is on in there whatever the storm is doing to everything else. */
const WINDOW_GLOW = '#ffcf8f';

/** Openings on the long wall facing the maze, plus one round the gable end. */
const WINDOWS: readonly { position: [number, number, number]; yaw: number }[] = [
  { position: [-2.6, 2, DEPTH / 2 + 0.06], yaw: 0 },
  { position: [2.6, 2, DEPTH / 2 + 0.06], yaw: 0 },
  { position: [WIDTH / 2 + 0.06, 2, -0.6], yaw: Math.PI / 2 },
];
const WINDOW_SIZE: [number, number, number] = [1.3, 0.95, 0.12];

/** How far the outermost water panel reaches on the north side. */
function moatEdge(panels: readonly Box[]): number {
  return panels.reduce((furthest, panel) => Math.min(furthest, boxMin(panel).z), 0);
}

export function HouseBackdrop({ built, grounds }: Props) {
  const { houseBandRadius } = buildExterior(built);
  const water = -moatEdge(grounds.waterPanels);

  // Measured outwards from the world centre, then negated below: north is -z. The band is
  // the whole of the decision; the water is only a floor under it, because this file is the
  // one that knows where the moat ends and `buildExterior` is not.
  const near = Math.max(houseBandRadius ?? 0, water + MIN_GAP);

  return (
    <group
      position={[grounds.exitGate.center.x, -GROUND_DROP, -(near + DEPTH / 2)]}
      rotation={[0, YAW, 0]}
    >
      <mesh
        position={[0, WALL_HEIGHT / 2, 0]}
        material={surfaceMaterial('wood', WOOD_COLOR, 4, 1.5)}
      >
        <boxGeometry args={[WIDTH, WALL_HEIGHT, DEPTH]} />
      </mesh>

      <mesh
        // The prism's flat face sits a third of its rise below its own middle, so this is
        // what drops the eaves exactly onto the top of the walls.
        position={[0, WALL_HEIGHT + ROOF_RISE / (2 * PRISM_HEIGHT), 0]}
        // Read right to left, the way three.js composes an XYZ euler: the z turn stands the
        // prism's cross-section up, the x turn lays its axis down along the ridge line.
        rotation={[-Math.PI / 2, 0, Math.PI / 2]}
        // Local axes, applied before that rotation: across the roof, along the ridge, up.
        scale={[ROOF_HALF_WIDTH / PRISM_HALF_BASE, WIDTH + 2 * EAVE, ROOF_RISE / PRISM_HEIGHT]}
        material={toonMaterial(ROOF_COLOR)}
      >
        <cylinderGeometry args={[1, 1, 1, 3]} />
      </mesh>

      {/* Rooted low enough to pass through the roof rather than balance on it. */}
      <mesh
        position={[-2.8, WALL_HEIGHT + ROOF_RISE - 0.6, 0]}
        material={toonMaterial(CHIMNEY_COLOR)}
      >
        <boxGeometry args={[0.8, 2.6, 0.8]} />
      </mesh>

      <mesh position={[0, 1.1, DEPTH / 2 + 0.06]} material={toonMaterial(DOOR_COLOR)}>
        <boxGeometry args={[1.1, 2.2, 0.12]} />
      </mesh>

      {/* One draw for every lit window, the `RoomTrim` idiom again. Basic rather than toon
          on purpose: these are the light, not something lit by it, and a shaded pane at
          this distance is indistinguishable from the wall around it. */}
      <Instances frustumCulled={false} limit={WINDOWS.length} range={WINDOWS.length}>
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial color={WINDOW_GLOW} />
        {WINDOWS.map((window, index) => (
          <Instance
            key={`window-${index}`}
            position={window.position}
            rotation={[0, window.yaw, 0]}
            scale={WINDOW_SIZE}
          />
        ))}
      </Instances>
    </group>
  );
}
